import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ScanSearch, AlertTriangle, CheckCircle2, Loader2, ShieldCheck,
  ScanLine, BadgeAlert, CheckSquare, Square, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

// iter188 — Super-Admin Ledger Sanity Scanner.
// iter197.1 — Per-row selection: every drift row appears as a checkbox.
// Admin can untick false positives before applying. The button live-shows
// the selected count + ₹ total. Backend now accepts {txn_ids:[...]} so
// only the selected rows get the payer credit.
export default function LedgerSanityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [excludedIds, setExcludedIds] = useState(() => new Set());
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  // iter233 — one-tap full ledger self-check (replay vs stored balances)
  const [selfCheck, setSelfCheck] = useState(null);
  const [selfChecking, setSelfChecking] = useState(false);

  const runSelfCheck = async () => {
    setSelfChecking(true);
    try {
      const res = await api.get('/super/ledger-self-check');
      setSelfCheck(res.data);
      if (res.data?.all_ok) toast.success('All ledgers clean — stored balances match the replayed history');
      else toast.warning(`${res.data?.groups_with_drift} group(s) have drift`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Self-check failed');
    } finally {
      setSelfChecking(false);
    }
  };

  // Re-init exclusion + expansion on every new report so admin gets a fresh slate.
  useEffect(() => {
    if (report?.drift_details) {
      setExcludedIds(new Set());
      // Auto-expand groups when row count is small (≤ 30); else admin opens manually.
      const totalRows = report.drift_details.length;
      if (totalRows <= 30) {
        const all = new Set();
        report.drift_details.forEach((d) => all.add(d.group_id));
        setExpandedGroups(all);
      } else {
        setExpandedGroups(new Set());
      }
    }
  }, [report]);

  // Pre-group drift_details by group_id (preserves API order within a group).
  const driftByGroup = useMemo(() => {
    const out = new Map();
    if (!report?.drift_details) return out;
    const groupNameMap = new Map(
      (report.per_group_drift || []).map((g) => [g.group_id, g.group_name]),
    );
    report.drift_details.forEach((d) => {
      const gid = d.group_id;
      if (!out.has(gid)) {
        out.set(gid, { group_id: gid, group_name: groupNameMap.get(gid) || 'Unknown', rows: [] });
      }
      out.get(gid).rows.push(d);
    });
    return out;
  }, [report]);

  const allDriftIds = useMemo(
    () => (report?.drift_details || []).map((d) => d.txn_id),
    [report],
  );

  const selectedIds = useMemo(
    () => allDriftIds.filter((id) => !excludedIds.has(id)),
    [allDriftIds, excludedIds],
  );

  const selectedTotal = useMemo(() => {
    const sel = new Set(selectedIds);
    return (report?.drift_details || [])
      .filter((d) => sel.has(d.txn_id))
      .reduce((s, d) => s + (d.amount || 0), 0);
  }, [selectedIds, report]);

  const toggleRow = useCallback((txnId) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(txnId)) next.delete(txnId);
      else next.add(txnId);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((gid) => {
    const groupBlock = driftByGroup.get(gid);
    if (!groupBlock) return;
    const ids = groupBlock.rows.map((r) => r.txn_id);
    const anyExcluded = ids.some((id) => excludedIds.has(id));
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (anyExcluded) {
        // At least one is excluded → re-include ALL.
        ids.forEach((id) => next.delete(id));
      } else {
        // All currently included → exclude ALL.
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [driftByGroup, excludedIds]);

  const toggleExpand = useCallback((gid) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  }, []);

  const selectAllNone = useCallback(() => {
    if (excludedIds.size === 0) {
      // Currently all selected → exclude all
      setExcludedIds(new Set(allDriftIds));
    } else {
      // Some/all excluded → re-include everything
      setExcludedIds(new Set());
    }
  }, [allDriftIds, excludedIds]);

  if (!user?.is_super_admin) {
    return (
      <div className="p-6 text-center" data-testid="ledger-sanity-forbidden">
        <ShieldCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-display text-xl font-bold">Super Admin Only</h2>
      </div>
    );
  }

  const runScan = async ({ clearApplyResult = true } = {}) => {
    setScanning(true);
    if (clearApplyResult) setApplyResult(null);
    try {
      const res = await api.post('/super/ledger-sanity-scan', { dry_run: true });
      setReport(res.data);
      const n = res.data.drift_rows_found || 0;
      if (n === 0) {
        toast.success('No ledger drift found — all payers correctly credited.');
      } else {
        toast.warning(`${n} drift row(s) found · ₹${(res.data.total_missing_credit || 0).toLocaleString('en-IN')} missing credit`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const runApply = async () => {
    if (!report || (report.drift_rows_found || 0) === 0) {
      toast.error('Run Scan first');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Tick at least one row to apply the repair');
      return;
    }
    const confirmMsg = `Apply ledger sanity repair?\n\n• ${selectedIds.length} row(s) selected (${excludedIds.size} excluded)\n• Credit ₹${selectedTotal.toLocaleString('en-IN')} back to payers\n• Tag selected rows with reimburse_from_fund=true\n• Idempotent — re-running finds nothing\n• Cannot be undone (manual reversal required if wrong)`;
    if (!window.confirm(confirmMsg)) return;
    setApplying(true);
    try {
      const res = await api.post('/super/ledger-sanity-scan', {
        dry_run: false,
        txn_ids: selectedIds,
      });
      setApplyResult(res.data);
      toast.success(res.data.message || 'Repair applied');
      // iter197.2 — refresh drift list but PRESERVE the apply-result card so
      // the admin can read which rows were applied / skipped / errored.
      await runScan({ clearApplyResult: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const driftCount = report?.drift_rows_found || 0;
  const totalDrift = report?.total_missing_credit || 0;
  const hasReport = !!report && driftCount > 0;
  const allSelected = excludedIds.size === 0;
  const noneSelected = selectedIds.length === 0;

  return (
    <div className="p-6 max-w-2xl lg:max-w-none mx-auto space-y-5" data-testid="ledger-sanity-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin')}
          className="p-2 -ml-2 rounded-full hover:bg-muted transition-colors"
          data-testid="ledger-sanity-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <ScanSearch className="w-6 h-6 text-purple-500" /> Ledger Sanity Scanner
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Backfill the missing payer credit on legacy expense approvals.
          </p>
        </div>
      </div>

      {/* iter233 — Ledger Self-Check */}
      <Card className="p-4 border-2 rounded-2xl bg-green-500/5 border-green-500/30" data-testid="ledger-self-check-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-green-600" /> Ledger Self-Check
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Replays every group's full transaction history and compares it with stored balances.
            </p>
          </div>
          <Button
            onClick={runSelfCheck}
            disabled={selfChecking}
            data-testid="ledger-self-check-btn"
            className="rounded-full shrink-0"
          >
            {selfChecking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            {selfChecking ? 'Checking…' : 'Run Check'}
          </Button>
        </div>
        {selfCheck && (
          <div className="mt-3 space-y-2" data-testid="ledger-self-check-results">
            <p className={`text-sm font-bold ${selfCheck.all_ok ? 'text-green-600' : 'text-yellow-600'}`}>
              {selfCheck.all_ok
                ? `✓ All ${selfCheck.total_groups} groups clean`
                : `${selfCheck.groups_with_drift} of ${selfCheck.total_groups} groups have drift`}
            </p>
            {selfCheck.groups.filter((g) => !g.ok).map((g) => (
              <div key={g.group_id} className="p-2 rounded-xl border border-yellow-500/40 bg-yellow-500/5 text-sm">
                <p className="font-semibold">{g.group_name}</p>
                {Math.abs(g.drift_fund) > 0.01 && (
                  <p className="text-xs text-muted-foreground">
                    Fund: stored ₹{g.fund_stored} vs expected ₹{g.fund_expected} (drift {g.drift_fund > 0 ? '+' : ''}{g.drift_fund})
                  </p>
                )}
                {g.coin_drifts.map((c) => (
                  <p key={c.user_id} className="text-xs text-muted-foreground">
                    {c.name}: stored ₹{c.stored} vs expected ₹{c.expected} (drift {c.drift > 0 ? '+' : ''}{c.drift})
                  </p>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Info Card */}
      <Card className="p-4 border-2 rounded-2xl bg-purple-500/5 border-purple-500/30">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-semibold">What does this do?</p>
            <p className="text-muted-foreground">
              Pre-iter188, when a member paid an expense and the group fund covered part of it, the
              fund deducted correctly but the payer was NOT credited the fund-covered amount. The
              payer absorbed the loss silently. This tool finds those rows and credits each payer
              back what they were originally owed.
            </p>
            <p className="text-muted-foreground italic mt-1">
              Untick any rows that look wrong before tapping Apply. Idempotent — re-runs find no
              drift after applying.
            </p>
          </div>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          size="lg"
          variant="outline"
          onClick={runScan}
          disabled={scanning || applying}
          data-testid="ledger-sanity-scan-btn"
          className="rounded-2xl h-14"
        >
          {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanLine className="w-4 h-4 mr-2" />}
          {scanning ? 'Scanning…' : 'Scan + Show Drift'}
        </Button>
        <Button
          size="lg"
          onClick={runApply}
          disabled={scanning || applying || driftCount === 0 || noneSelected}
          data-testid="ledger-sanity-apply-btn"
          className="rounded-2xl h-14 bg-purple-600 hover:bg-purple-700 disabled:opacity-40"
        >
          {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BadgeAlert className="w-4 h-4 mr-2" />}
          {applying
            ? 'Applying…'
            : hasReport
              ? `Apply on ${selectedIds.length}`
              : 'Apply Repair'}
        </Button>
      </div>

      {/* Selection summary chip */}
      {hasReport && (
        <Card
          className="p-3 border-2 rounded-2xl flex items-center gap-3 bg-card"
          data-testid="ledger-sanity-selection-summary"
        >
          <button
            type="button"
            onClick={selectAllNone}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-muted transition-colors text-xs font-semibold"
            data-testid="ledger-sanity-toggle-all"
          >
            {allSelected
              ? <><CheckSquare className="w-4 h-4 text-purple-600" /> All selected</>
              : noneSelected
                ? <><Square className="w-4 h-4 text-muted-foreground" /> None selected</>
                : <><Square className="w-4 h-4 text-amber-600" /> Some excluded</>}
          </button>
          <div className="flex-1 min-w-0 text-xs text-muted-foreground">
            {selectedIds.length} of {driftCount} row(s)
            <span className="mx-1.5 text-muted-foreground/50">·</span>
            <span className="font-mono text-purple-600 font-semibold">
              ₹{selectedTotal.toLocaleString('en-IN')}
            </span>
            {excludedIds.size > 0 && (
              <span className="ml-1.5 text-amber-600 font-semibold">
                ({excludedIds.size} excluded)
              </span>
            )}
          </div>
        </Card>
      )}

      {/* Apply Result */}
      {applyResult && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="p-4 border-2 rounded-2xl bg-green-500/10 border-green-500/40" data-testid="ledger-sanity-apply-result">
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <div className="text-sm flex-1 min-w-0">
                <p className="font-semibold">{applyResult.message}</p>
                <p className="text-muted-foreground mt-1">
                  Applied: <span className="font-mono">{applyResult.applied}</span> · Credited:{' '}
                  <span className="font-mono">₹{(applyResult.total_corrected || 0).toLocaleString('en-IN')}</span> · Groups:{' '}
                  <span className="font-mono">{applyResult.groups_corrected}</span>
                </p>
                {applyResult.errors?.length > 0 && (
                  <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs" data-testid="ledger-sanity-apply-errors">
                    <p className="font-semibold text-amber-700 dark:text-amber-300 mb-1">
                      {applyResult.errors.length} row(s) skipped:
                    </p>
                    <ul className="space-y-1 max-h-40 overflow-y-auto">
                      {applyResult.errors.slice(0, 30).map((e, i) => (
                        <li key={i} className="text-muted-foreground">
                          • {e.user_name || 'Unknown'}
                          {typeof e.amount === 'number' ? ` · ₹${e.amount}` : ''}
                          {' — '}
                          <span className="font-mono text-amber-700 dark:text-amber-300">
                            {e.reason === 'orphan_group' ? 'Group deleted (auto-tagged)' : (e.error || 'unknown error')}
                          </span>
                        </li>
                      ))}
                      {applyResult.errors.length > 30 && (
                        <li className="text-muted-foreground italic">
                          + {applyResult.errors.length - 30} more …
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Report */}
      {report && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="ledger-sanity-report">
          <Card className={`p-4 border-2 rounded-2xl ${driftCount > 0 ? 'border-yellow-500/40 bg-yellow-500/5' : 'border-green-500/40 bg-green-500/5'}`}>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Scanned</p>
                <p className="font-display text-xl font-bold" data-testid="report-scanned">{report.scanned_expense_deducts}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Drift Rows</p>
                <p className="font-display text-xl font-bold text-yellow-600" data-testid="report-drift-count">{driftCount}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Missing Credit</p>
                <p className="font-display text-xl font-bold text-purple-600" data-testid="report-total-drift">
                  ₹{totalDrift.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
            {report.message && (
              <p className="text-xs text-muted-foreground text-center mt-3 italic">{report.message}</p>
            )}
          </Card>

          {/* Per-group drift WITH expandable per-row checkboxes — replaces the
              read-only summary card + the separate drift_details card. */}
          {hasReport && driftByGroup.size > 0 && (
            <Card className="p-2 border-2 rounded-2xl" data-testid="report-per-group">
              <h3 className="font-semibold text-sm px-2 pt-2 pb-2">Per-Group Drift · tap a group to view + tick rows</h3>
              <div className="divide-y divide-border">
                {Array.from(driftByGroup.values()).map((g) => {
                  const ids = g.rows.map((r) => r.txn_id);
                  const includedCount = ids.filter((id) => !excludedIds.has(id)).length;
                  const groupTotal = g.rows
                    .filter((r) => !excludedIds.has(r.txn_id))
                    .reduce((s, r) => s + (r.amount || 0), 0);
                  const isExpanded = expandedGroups.has(g.group_id);
                  const allInc = includedCount === g.rows.length;
                  const noneInc = includedCount === 0;
                  return (
                    <div key={g.group_id} className="py-1.5" data-testid={`ledger-sanity-group-${g.group_id}`}>
                      <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/40">
                        <button
                          type="button"
                          aria-label={allInc ? 'Exclude all in group' : 'Include all in group'}
                          onClick={() => toggleGroup(g.group_id)}
                          className="shrink-0 p-1 -m-1"
                          data-testid={`ledger-sanity-group-toggle-${g.group_id}`}
                        >
                          {allInc
                            ? <CheckSquare className="w-5 h-5 text-purple-600" />
                            : noneInc
                              ? <Square className="w-5 h-5 text-muted-foreground" />
                              : <Square className="w-5 h-5 text-amber-600" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleExpand(g.group_id)}
                          className="flex-1 min-w-0 flex items-center gap-1.5 text-left"
                          data-testid={`ledger-sanity-group-expand-${g.group_id}`}
                        >
                          {isExpanded
                            ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                          <span className="font-medium truncate text-sm">{g.group_name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            · {includedCount}/{g.rows.length}
                          </span>
                        </button>
                        <span className="font-mono text-sm text-purple-600 shrink-0 ml-2">
                          ₹{groupTotal.toLocaleString('en-IN')}
                        </span>
                      </div>
                      {isExpanded && (
                        <div className="pl-8 pr-2 pb-2 space-y-1.5" data-testid={`ledger-sanity-group-rows-${g.group_id}`}>
                          {g.rows.map((d) => {
                            const isIncluded = !excludedIds.has(d.txn_id);
                            return (
                              <button
                                type="button"
                                key={d.txn_id}
                                onClick={() => toggleRow(d.txn_id)}
                                className={`w-full flex items-start gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${
                                  isIncluded
                                    ? 'hover:bg-muted/50'
                                    : 'opacity-60 line-through hover:opacity-90'
                                }`}
                                data-testid={`ledger-sanity-row-${d.txn_id}`}
                              >
                                <span className="shrink-0 mt-0.5">
                                  {isIncluded
                                    ? <CheckSquare className="w-4 h-4 text-purple-600" />
                                    : <Square className="w-4 h-4 text-muted-foreground" />}
                                </span>
                                <div className="flex-1 min-w-0 text-xs">
                                  <p className="font-semibold truncate">
                                    {d.user_name} · {d.expense_description || '(no description)'}
                                  </p>
                                  <p className="text-muted-foreground font-mono">
                                    Missing credit: <span className="text-purple-600">+₹{d.amount}</span>
                                    {' · '}Expense total: ₹{d.expense_amount}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {report.drift_details_truncated && (
                <p className="text-xs text-muted-foreground italic px-3 py-2">
                  Showing first 500 rows · the remainder will still be applied if you don&apos;t
                  exclude (server-side selective filter applies to the listed txn_ids).
                </p>
              )}
            </Card>
          )}

          {report.per_user_drift_top50?.length > 0 && (
            <Card className="p-4 border-2 rounded-2xl" data-testid="report-per-user">
              <h3 className="font-semibold text-sm mb-3">Top affected payers (top 50)</h3>
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {report.per_user_drift_top50.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between text-sm py-1">
                    <span className="font-medium truncate">{u.user_name}</span>
                    <span className="font-mono text-purple-600 shrink-0 ml-3">
                      +₹{(u.missing_credit || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {(report.skipped_fund_paid > 0 || report.skipped_direct_payment > 0 || report.skipped_no_expense > 0) && (
            <Card className="p-3 border rounded-xl bg-muted/30">
              <p className="text-xs text-muted-foreground">
                Skipped (already correct):{' '}
                {report.skipped_fund_paid} fund-paid · {report.skipped_direct_payment} direct-payment ·{' '}
                {report.skipped_no_expense} orphan rows
              </p>
            </Card>
          )}
        </motion.div>
      )}

      {!report && !scanning && (
        <Card className="p-8 border-2 border-dashed rounded-2xl text-center">
          <ScanSearch className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Tap <strong>Scan + Show Drift</strong> to begin a dry-run audit.
          </p>
        </Card>
      )}
    </div>
  );
}
